import { useElementSize } from "@mantine/hooks";
import {
  formatDateWithDynamicTimezone,
  formatIDR
} from "@marketplace/shared/utils";
import {
  ActionIcon,
  Button,
  ConditionalRendering,
  Text,
  UnstyledButton
} from "@marketplace/ui";
import clsx from "clsx";
import { useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlinePayments,
  MdOutlineTextSnippet,
  MdSwapHoriz
} from "react-icons/md";

import { ProtectedImage } from "@/components";
import {
  TransactionStatusPill,
  type TransactionStatusPillStatus
} from "@/modules/shared";
import type { IconProps } from "@/types";

import { integrationStatusModalAtom } from "../../atoms/integration-status-atom";
import { payoutStatusModalAtom } from "../../atoms/payout-status-atom";
import { sendAccessModalAtom } from "../../atoms/send-access-atom";
import type { OrderListItem, OrderListItemOrderItem } from "../../types";
import { RecurringBadge } from "../recurring-badge";

type OrderItemBodyProps = {
  items: OrderListItemOrderItem[];
  totalPayment: number;
  paymentStatus: TransactionStatusPillStatus;
  isExpanded: boolean;
  seeDetailButtonOnChange: () => void;
};

type OrderItemFooterProps = {
  paymentStatus: TransactionStatusPillStatus;
  orderItems: OrderListItemOrderItem[];
  orderId: string;
  paymentMethod?: string;
  expiresAt?: string;
};

type OrderItemProps = {
  orderItem: OrderListItem;
  seeDetailButtonOnChange: () => void;
};

const DoubleArrow = (props: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      fill="none"
      viewBox="0 0 18 18"
      {...props}
    >
      <path
        fill="#8A98A6"
        d="m9.435 15.06 4.237-3.03c.36-.255.578-.667.578-1.11 0-1.117-1.26-1.762-2.168-1.117L9 12 5.918 9.803c-.908-.653-2.168 0-2.168 1.117 0 .443.217.863.577 1.118l4.238 3.03c.263.18.607.18.87-.008"
      ></path>
      <path
        fill="#8A98A6"
        d="m9.435 9.81 4.237-3.022c.36-.263.578-.675.578-1.118 0-1.117-1.26-1.77-2.168-1.117L9 6.75 5.918 4.553C5.01 3.9 3.75 4.553 3.75 5.67c0 .443.217.863.577 1.118l4.238 3.03c.263.18.607.18.87-.008"
      ></path>
    </svg>
  );
};

const OrderItemBody = ({
  items,
  totalPayment,
  paymentStatus,
  isExpanded,
  seeDetailButtonOnChange
}: OrderItemBodyProps) => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.orderDetailModal.table.columns"
  });
  const { t: tOrder } = useTranslation("order", {
    keyPrefix: "orderListPage"
  });
  const { ref, height } = useElementSize();
  const { ref: contentRef, height: contentHeight } = useElementSize();

  const setSendAccessModal = useSetAtom(sendAccessModalAtom);
  const resetSendAccessModal = useResetAtom(sendAccessModalAtom);

  const handleSendAccessButtonOnChange = (orderItemId: number) => {
    resetSendAccessModal();
    const orderData = items.find((o) => o.id === orderItemId);
    setSendAccessModal({ visible: true, data: orderData! });
  };

  const showSubmitAccessButton =
    paymentStatus === "paid" || paymentStatus === "done";

  return (
    <div
      className={clsx(
        "border-solid border-0 border-y border-mischka-100 duration-300",
        items.length > 1 && "transition-[max-height]"
      )}
      style={{
        minHeight: 86,
        maxHeight: isExpanded ? contentHeight : height + 28,
        overflow: "hidden"
      }}
    >
      <div ref={contentRef}>
        {items.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            ref={index === 0 ? ref : null}
            className={clsx(
              "flex flex-col md:flex-row md:items-center p-[14px] gap-y-4",
              index > 0 && "border-solid border-0 border-t border-mischka-100"
            )}
          >
            <div className="flex flex-col md:flex-row md:gap-x-[14px] flex-1 gap-y-4">
              {/* Product Image and Name */}
              <div className="flex items-center gap-x-[14px] md:min-w-0 md:flex-[0_0_auto] md:w-auto">
                <div className="shrink-0 relative w-[58px] h-[58px] bg-fjord-grey-100 overflow-hidden rounded">
                  <ProtectedImage
                    src={`media/${item.product_media_path}`}
                    alt={`${item.product_name} image`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col gap-y-1 text-fjord-grey-500 min-w-0 flex-1 md:w-[140px] lg:w-[164px]">
                  <Text
                    type="heading"
                    size="xxs"
                    weight="bold"
                    className="line-clamp-2"
                  >
                    {item.product_name}
                  </Text>
                  <Text size="xs" className="truncate">
                    {tOrder("productBy")}:{" "}
                    {item.product_company_brand_name ||
                      item.product_company_name}
                  </Text>
                </div>
              </div>

              {/* Divider - Hidden on mobile */}
              <div className="hidden md:block shrink-0 w-[1px] self-stretch bg-mischka-100" />

              {/* Package, Variant, Total Payment - Responsive Grid */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-x-2 md:gap-x-3 lg:gap-x-4 text-fjord-grey-500 md:flex-1">
                <div className="flex-1 min-w-0">
                  <Text className="mb-2 md:mb-[18px]">{t("package")}</Text>
                  <Text
                    type="heading"
                    size="s"
                    className="truncate"
                    title={item.package_name}
                  >
                    {item.package_name}
                  </Text>
                </div>
                <div className="flex-1 min-w-0">
                  <Text className="mb-2 md:mb-[18px]">{t("variant")}</Text>
                  <Text
                    type="heading"
                    size="s"
                    className="truncate"
                    title={item.variant_name}
                  >
                    {item.variant_name}
                  </Text>
                </div>
                <div className="flex-1 min-w-0">
                  {index === 0 && (
                    <>
                      <Text className="mb-2 md:mb-[18px]">
                        {tOrder("totalPayment")}
                      </Text>
                      <Text
                        type="heading"
                        size="s"
                        className="truncate"
                        title={formatIDR(totalPayment)}
                      >
                        {formatIDR(totalPayment)}
                      </Text>
                    </>
                  )}
                </div>
              </div>

              {/* Status Section - Only on first item */}
              {index === 0 && (
                <>
                  {/* Divider - Hidden on mobile */}
                  <div className="hidden md:block shrink-0 w-[1px] self-stretch bg-mischka-100" />

                  <div className="md:min-w-0 md:w-auto lg:min-w-[140px] xl:min-w-[160px]">
                    <Text className="mb-2 md:mb-[18px] text-fjord-grey-500">
                      {tOrder("status")}
                    </Text>
                    <TransactionStatusPill status={paymentStatus} />
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col justify-center gap-2 min-w-[179px]">
              {index === 0 && (
                <Button
                  variant="secondary"
                  rightSection={<MdOutlineTextSnippet />}
                  size="sm"
                  onClick={seeDetailButtonOnChange}
                >
                  {tOrder("orderDetail")}
                </Button>
              )}
              <ConditionalRendering condition={showSubmitAccessButton}>
                <Button
                  variant="primary"
                  rightSection={<MdOutlineTextSnippet />}
                  size="sm"
                  onClick={() => handleSendAccessButtonOnChange(item.id)}
                >
                  {paymentStatus !== "done"
                    ? tOrder("submitAccess")
                    : tOrder("resubmitAccess")}
                </Button>
              </ConditionalRendering>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OrderItemFooter = ({
  paymentStatus,
  orderId,
  paymentMethod,
  expiresAt
}: OrderItemFooterProps) => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage"
  });
  const setIntegrationStatusModal = useSetAtom(integrationStatusModalAtom);
  const setPayoutStatusModal = useSetAtom(payoutStatusModalAtom);

  const handleIntegrationClick = () => {
    setIntegrationStatusModal({ visible: true, orderId });
  };

  const handlePayoutClick = () => {
    setPayoutStatusModal({ visible: true, orderId });
  };

  const showActionButtons =
    paymentStatus === "paid" || paymentStatus === "done";

  return (
    <div className="flex items-center justify-between p-[14px] h-[50px]">
      <div className="flex items-center gap-x-[14px]">
        <ConditionalRendering condition={!!paymentMethod}>
          <div className="flex items-center gap-x-1">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("paymentMethod")}
            </Text>
            <Text component="span">{paymentMethod || "-"}</Text>
          </div>
        </ConditionalRendering>
        <ConditionalRendering condition={!!expiresAt}>
          <div className="flex items-center gap-x-1">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("dueDate")}
            </Text>
            <Text
              component="span"
              type="heading"
              size="xxs"
              className="ml-1 text-[#FA622D]"
            >
              {expiresAt ? formatDateWithDynamicTimezone(expiresAt) : "-"}
            </Text>
          </div>
        </ConditionalRendering>
      </div>

      <ConditionalRendering condition={showActionButtons}>
        <div className="flex items-center gap-x-[8px]">
          <UnstyledButton
            className="rounded border-solid border border-mischka-400 text-fjord-grey-500 px-[10px] py-[6px] flex items-center gap-x-[6px] hover:bg-gray-100"
            onClick={handlePayoutClick}
          >
            <Text size="s">{t("payout")}</Text>
            <MdOutlinePayments size={14} />
          </UnstyledButton>
          <UnstyledButton
            className="rounded bg-fjord-grey-500 text-fjord-grey-100 px-[10px] py-[6px] flex items-center gap-x-[6px] hover:bg-gray-600"
            onClick={handleIntegrationClick}
          >
            <Text size="s">{t("integration")}</Text>
            <MdSwapHoriz size={14} />
          </UnstyledButton>
        </div>
      </ConditionalRendering>
    </div>
  );
};

export const OrderItem = ({
  orderItem,
  seeDetailButtonOnChange
}: OrderItemProps) => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage"
  });
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="relative w-full bg-brand-white rounded-lg border-solid border border-mischka-400">
      {orderItem.order_items.length > 1 && (
        <ActionIcon
          radius="xl"
          variant="white"
          onClick={() => setIsExpanded((prev) => !prev)}
          classNames={{
            root: clsx(
              "absolute bottom-[calc(86px-16px)] left-1/2 transform -translate-x-1/2 w-8 h-8 z-10 shadow-md"
            ),
            icon: clsx(
              "transform transition-transform duration-300",
              isExpanded && "rotate-180"
            )
          }}
        >
          <DoubleArrow />
        </ActionIcon>
      )}

      <div className="p-[14px] text-fjord-grey-500 flex justify-between items-center">
        <div className="flex items-center">
          <Text type="heading" size="xxs" weight="bold" className="mr-1">
            {orderItem.customer.name}
          </Text>
          <Text className="mr-1">
            {formatDateWithDynamicTimezone(orderItem.order_created_at)}
          </Text>
          {orderItem.order_type === "recurring" && <RecurringBadge />}
        </div>
        <div className="flex items-center">
          <Text type="heading" size="xxs" weight="bold">
            {t("orderId")}
          </Text>
          <Text className="ml-1">{orderItem.id}</Text>
        </div>
      </div>
      <OrderItemBody
        items={orderItem.order_items}
        totalPayment={
          orderItem.order_status == "order_created"
            ? orderItem.total_price
            : orderItem.final_total_price
        }
        paymentStatus={orderItem.order_status as TransactionStatusPillStatus}
        isExpanded={isExpanded}
        seeDetailButtonOnChange={seeDetailButtonOnChange}
      />
      <OrderItemFooter
        paymentStatus={orderItem.order_status as TransactionStatusPillStatus}
        orderItems={orderItem.order_items}
        orderId={orderItem.id}
        paymentMethod={orderItem.payment?.method_name}
        expiresAt={orderItem.expires_at}
      />
    </div>
  );
};
